import axios from '../utils/axiosConfig';

class UserStatsService {
  constructor() {
    this.statsCache = new Map();
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  async getUserStats(userId, forceRefresh = false) {
    const cacheKey = `user_${userId}_stats`;
    const cached = this.statsCache.get(cacheKey);

    // Return cached data if valid and not forcing refresh
    if (cached && !forceRefresh && (Date.now() - cached.timestamp) < this.cacheDuration) {
      return cached.data;
    }

    try {
      const response = await axios.get(`/community/users/${userId}/stats`);
      const stats = response.data.data;
      
      // Cache the result
      this.statsCache.set(cacheKey, {
        data: stats,
        timestamp: Date.now()
      });

      return stats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Return cached data if available, even if expired
      if (cached) return cached.data;
      
      // Return default stats
      return {
        postsCount: 0,
        followersCount: 0,
        followingCount: 0
      };
    }
  }

  async getMyStats(forceRefresh = false) {
    // Try to get from localStorage first
    if (!forceRefresh) {
      const cached = localStorage.getItem('my_stats');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < this.cacheDuration) {
          return data;
        }
      }
    }

    try {
      const response = await axios.get('/community/users/me/stats');
      const stats = response.data.data;
      
      // Cache in localStorage
      localStorage.setItem('my_stats', JSON.stringify({
        data: stats,
        timestamp: Date.now()
      }));

      return stats;
    } catch (error) {
      console.error('Error fetching my stats:', error);
      // Return from localStorage even if expired
      const cached = localStorage.getItem('my_stats');
      if (cached) {
        return JSON.parse(cached).data;
      }
      
      // Return default stats
      return {
        postsCount: 0,
        followersCount: 0,
        followingCount: 0
      };
    }
  }

  clearCache() {
    this.statsCache.clear();
    localStorage.removeItem('my_stats');
  }

  // Update stats locally when we know they've changed
  updateStats(userId, updates) {
    if (userId === 'me') {
      const cached = localStorage.getItem('my_stats');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        localStorage.setItem('my_stats', JSON.stringify({
          data: { ...data, ...updates },
          timestamp
        }));
      }
    } else {
      const cacheKey = `user_${userId}_stats`;
      const cached = this.statsCache.get(cacheKey);
      if (cached) {
        this.statsCache.set(cacheKey, {
          data: { ...cached.data, ...updates },
          timestamp: cached.timestamp
        });
      }
    }
  }

  // Increment/decrement counts
  incrementPostCount(userId = 'me') {
    this.updateStats(userId, (prev) => ({
      postsCount: (prev.postsCount || 0) + 1
    }));
  }

  decrementPostCount(userId = 'me') {
    this.updateStats(userId, (prev) => ({
      postsCount: Math.max(0, (prev.postsCount || 1) - 1)
    }));
  }

  incrementFollowers(userId = 'me') {
    this.updateStats(userId, (prev) => ({
      followersCount: (prev.followersCount || 0) + 1
    }));
  }

  decrementFollowers(userId = 'me') {
    this.updateStats(userId, (prev) => ({
      followersCount: Math.max(0, (prev.followersCount || 1) - 1)
    }));
  }

  incrementFollowing(userId = 'me') {
    this.updateStats(userId, (prev) => ({
      followingCount: (prev.followingCount || 0) + 1
    }));
  }

  decrementFollowing(userId = 'me') {
    this.updateStats(userId, (prev) => ({
      followingCount: Math.max(0, (prev.followingCount || 1) - 1)
    }));
  }
}

export default new UserStatsService();